<script setup lang="ts">
import { type PropType } from "vue";
import type { UIColor } from "../../..";
import Icon from "../../atoms/Icon/Icon.vue";

defineProps({
  id: {
    type: String,
    default: "",
  },

  /*
      Should the cell be fixed to the left
      values: true, false (default)
    */
  fixed: {
    type: Boolean,
    default: false,
  },

  /*
      Cell column-span
    */
  colSpan: {
    type: [Number, String],
    default: "1",
  },

  /*
      Omit padding
    */
  omitPadding: {
    type: Boolean,
    default: false,
  },

  /*
      Cell width override IN PERCENTAGES ONLY
      This distrubts table flow, use with caution
    */
  width: {
    type: String,
    default: "auto",
  },

  /*
      Should the cell align text to the top of the cell
      values: true, false (default)
    */
  textAlignTop: {
    type: Boolean,
    default: false,
  },

  /*
      Should the cell align text to the end of the cell
      values: true, false (default)
    */
  textAlignEnd: {
    type: Boolean,
    default: false,
  },

  /*
      Should the cell align text to the center of the cell
      values: true, false (default)
    */
  textAlignCenter: {
    type: Boolean,
    default: false,
  },

  /*
      Should sorting be enabled on the cell
    */
  sortingEnable: {
    type: Boolean,
    default: false,
  },

  /*
      Switches icon for desc/asc
    */
  sortingOrder: {
    type: String as PropType<"desc" | "asc">,
    default: "desc",
  },

  /*
      Shows if the cell is applying sorting
    */
  sortingActive: {
    type: Boolean,
    default: false,
  },

  /*
      Set the color of the ordering icons
    */
  sortingIconColor: {
    type: String as PropType<UIColor>,
    default: "--color-primary",
  },

  /*
      Cell contains inputs
      Adjust padding to conform
    */
  containsInput: {
    type: Boolean,
    default: false,
  },

  visible: {
    type: Boolean,
    default: true,
  },

  onClick: {
    type: Function as PropType<() => void>,
    required: false,
  },
});
</script>

<template>
  <td
    v-if="visible"
    :id="id"
    :class="[
      'simple-table-cell',
      $style.td,
      $style[omitPadding ? 'omitPadding' : ''],
      $style[textAlignTop ? 'textAlignTop' : ''],
      $style[textAlignEnd ? 'textAlignEnd' : ''],
      $style[textAlignCenter ? 'textAlignCenter' : ''],
      $style[fixed ? 'fixed' : ''],
      $style[containsInput ? 'containsInput' : ''],
      fixed ? 'simple-table-cell-fixed' : '',
    ]"
    :colspan="colSpan"
    :style="{ width: width }"
  >
    <div v-if="$slots.background" :class="$style.slotBackground">
      <slot name="background" />
    </div>

    <button v-if="sortingEnable" @click="onClick" :class="$style.button">
      <slot />

      <span v-if="sortingActive" :class="$style.icon">
        <Icon
          v-if="sortingOrder === 'desc'"
          :id="'arrow_drop_down'"
          size="large"
          :color="sortingIconColor"
        />

        <Icon
          v-else
          :id="'arrow_drop_up'"
          size="large"
          :color="sortingIconColor"
        />
      </span>

      <span
        v-else
        :class="['simple-table-cell-sort-icon-transparent', $style.icon]"
      >
        <Icon :id="'transparent'" size="large" color="--color-transparent" />
      </span>
    </button>

    <div
      v-else
      :class="[
        'simple-table-cell-controls-container',
        $style.controlsContainer,
      ]"
    >
      <slot v-if="!$slots.background" />
    </div>
  </td>
</template>

<style module>
.td {
  padding: 0.75rem 1rem;
  line-height: 1rem;
  position: relative;
  white-space: nowrap;
  vertical-align: middle;
  position: relative;
}

.slotBackground {
  position: absolute;
  width: 100%;
  height: calc(100% - 1px);
  top: 1px;
  left: 0;
  z-index: -1;
}

.button {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 0;
  height: 1rem;
  border-style: none;
  background: inherit;
  cursor: pointer;
}

.icon {
  display: inline-flex;
}

.omitPadding {
  padding: unset;
}

.textAlignTop {
  vertical-align: top;
}

.textAlignCenter {
  text-align: center;
}

.textAlignEnd {
  text-align: end;
}

.fixed {
  position: sticky !important;
  left: 0;
  z-index: 1;
}

.containsInput {
  padding: 3.25px 1rem;
}

:global(
    .simple-table-row.hoverEffectDisable
      .simple-table-cell:last-child
      .simple-table-cell-sort-icon-transparent
  ) {
  display: none;
}

:global(.simple-table-row.simple-table-row-fixed td.simple-table-cell-fixed) {
  z-index: 3;
}

:global(
    .simple-table-row:not(.simple-table-row-fixed) td.simple-table-cell-fixed
  ) {
  z-index: 2;
}
</style>
